/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DetailsRow, IColumn, IDetailsRowProps } from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { Badge } from 'src/core/components/Badge';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { partial } from 'src/core/utils/function';
import { phraseListTableMessages } from 'src/luis/appShell/buildPane/featuresPage/components/phraseListTableMessages';
import { FeatureWithModels } from 'src/luis/modules/features/types/FeatureWithModels';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';
import { ModelType } from 'src/luis/types/ModelType';

const { styleComponent } = getStylistV2('PhraseListTable');

const StyledUsedByStack = styleComponent<IStackProps>(Stack)('StyledUsedByStack', {
    flexWrap: 'wrap',
    whiteSpace: 'pre-wrap'
});

const UsedByText = styleComponent<LuisTextProps>(LuisText)('UsedByText', {
    width: '100%',
    display: 'inline-block'
});

export type PhraseListTableProps = TableProps<FeatureWithModels<PhraseList>> & {
    /**
     * A callback for when a phrase list link is clicked.
     */
    onPhraseListClick?: (item: FeatureWithModels<PhraseList>) => void;
};

/**
 * Displays a table of phrase lists.
 *
 * @param props The input properties for the table.
 */
export const PhraseListTable = (props: PhraseListTableProps) => {
    const { selection, items, onPhraseListClick, sortKey, ...rest } = props;
    const { localize } = useLocalization();

    /**
     * A custom renderer for the phrase list name.
     *
     * @param phraseList The phrase list being rendered.
     */
    const renderName = (phraseList: FeatureWithModels<PhraseList>) => (
        <Link styles={{ root: { fontSize: '14px' } }} onClick={partial(onPhraseListClick, phraseList)} data-selection-disabled>
            {phraseList.name}
        </Link>
    );

    /**
     * A custom renderer for the phrase list types.
     */
    const renderType = () => <LuisText>{localize(phraseListTableMessages.phraseList)}</LuisText>;

    /**
     * A custom renderer for the phrase list exchangeable state.
     *
     * @param phraseList The phrase list being rendered.
     */
    const renderUsedBy = (phraseList: FeatureWithModels<PhraseList>) => (
        <StyledUsedByStack horizontal verticalAlign="start">
            {phraseList.isGlobal ? (
                <LuisText key={phraseList.id}>{localize(phraseListTableMessages.global)}</LuisText>
            ) : (
                phraseList.usedBy.map(u => (
                    <UsedByText>
                        <LuisText>{u.name}</LuisText>
                        <Badge text={localize(phraseListTableMessages[u.typeId === ModelType.Intent ? 'intent' : 'entity'])} />
                    </UsedByText>
                ))
            )}
        </StyledUsedByStack>
    );

    const columns: IColumn[] = [
        {
            key: 'name',
            name: localize(phraseListTableMessages.nameColumnHeader),
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            isCollapsible: false,
            onRender: renderName,
            isSorted: sortKey && !!(sortKey.key === 'name'),
            isSortedDescending: sortKey && !!(sortKey.key === 'name') && sortKey?.order === 'desc'
        },
        {
            key: 'type',
            name: localize(phraseListTableMessages.typeColumnHeader),
            fieldName: 'type',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            isCollapsible: true,
            onRender: renderType
        },
        {
            key: 'usedBy',
            name: localize(phraseListTableMessages.usedByColumnHeader),
            fieldName: 'usedBy',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            isCollapsible: true,
            onRender: renderUsedBy
        }
    ];

    const renderRow = (rowProps?: IDetailsRowProps) => (
        <DetailsRow {...rowProps} styles={{ cell: { display: 'inline-flex', alignItems: 'flex-start' } }} />
    );

    return (
        <Table<FeatureWithModels<PhraseList>>
            emptyPlaceholder={localize(phraseListTableMessages.emptyTablePlaceholder)}
            columns={columns}
            items={items}
            selection={selection}
            onRenderRow={renderRow}
            {...rest}
        />
    );
};
